import { createBrowserRouter, Navigate } from 'react-router';
import { AppShell } from '@/components/layout/app-shell';
import {
  RequireAuth,
  RequireAdmin,
  RequireSupportStaff,
  RedirectIfAuthenticated,
} from '@/components/layout/route-guards';
import { RouteErrorBoundary } from '@/components/layout/route-error-boundary';
import { AddMenuPage } from '@/routes/add/add-menu-page';
import { ImportUrlPage } from '@/routes/add/import-url-page';
import { ImportJobPage } from '@/routes/add/import-job-page';
import { TextGenerationPage } from '@/routes/add/text-page';
import { ImagesGenerationPage } from '@/routes/add/images-page';
import { VideoGenerationPage } from '@/routes/add/video-page';
import { AskSourcesPage } from '@/routes/add/ask-page';
import { AiReviewPage } from '@/routes/add/ai-review-page';
import { ConversationsPage } from '@/routes/assistant/conversations-page';
import { ConversationPage } from '@/routes/assistant/conversation-page';
import { CollectionsPage } from '@/routes/collections/collections-page';
import { CollectionDetailPage } from '@/routes/collections/collection-detail-page';
import { ShoppingListsPage } from '@/routes/shopping/shopping-lists-page';
import { ShoppingListPage } from '@/routes/shopping/shopping-list-page';
import { ReceiptCapturePage } from '@/routes/shopping/receipt-capture-page';
import { ReceiptReviewPage } from '@/routes/shopping/receipt-review-page';
import { ReceiptsPage } from '@/routes/shopping/receipts-page';
import { CatalogStorePricesPage } from '@/routes/catalog/store-prices-page';
import { MealPlanPage } from '@/routes/meal-plan/meal-plan-page';
import { PantryPage } from '@/routes/pantry/pantry-page';
import { HouseholdsPage } from '@/routes/households/households-page';
import { HouseholdDetailPage } from '@/routes/households/household-detail-page';
import { InvitationPage } from '@/routes/households/invitation-page';
import { PublicRecipePage } from '@/routes/public/public-recipe-page';
import { AdminDashboardPage } from '@/routes/admin/dashboard-page';
import { AdminUserDetailPage } from '@/routes/admin/user-detail-page';
import { AdminUsersPage } from '@/routes/admin/users-page';
import { AdminJobsPage } from '@/routes/admin/jobs-page';
import { AdminFlagsPage } from '@/routes/admin/flags-page';
import { AdminAiProvidersPage } from '@/routes/admin/ai-providers-page';
import { AdminNutritionPage } from '@/routes/admin/nutrition-page';
import { AdminUnitsPage } from '@/routes/admin/units-page';
import { AdminCategoriesPage } from '@/routes/admin/categories-page';
import { AdminScraperPage } from '@/routes/admin/scraper-page';
import { AdminReportsPage } from '@/routes/admin/reports-page';
import { AdminAuditPage } from '@/routes/admin/audit-page';
import { AdminHelpPage } from '@/routes/admin/help-page';
import { AdminMailPage } from '@/routes/admin/mail-page';
import { AdminMailTemplatesPage } from '@/routes/admin/mail-templates-page';
import { AdminMailTemplateEditorPage } from '@/routes/admin/mail-template-editor-page';
import { AdminMailDeliveryPage } from '@/routes/admin/mail-delivery-page';
import { AdminSupportPage } from '@/routes/admin/support-page';
import { AdminSupportConversationPage } from '@/routes/admin/support-conversation-page';
import { lazyPage } from '@/components/layout/lazy-page';
import { NotFoundPage } from '@/components/layout/not-found-page';
import { LoginPage } from '@/routes/auth/login';
import { OidcCallbackPage } from '@/routes/auth/oidc-callback';
import { RegisterPage } from '@/routes/auth/register';
import { ForgotPasswordPage } from '@/routes/auth/forgot-password';
import { ResetPasswordPage } from '@/routes/auth/reset-password';
import { VerifyEmailPage } from '@/routes/auth/verify-email';
import { DashboardPage } from '@/routes/dashboard/dashboard';
import { RecipeLibraryPage } from '@/routes/recipes/library';
import { RecipeDetailPage } from '@/routes/recipes/detail';
import { RecipeHistoryPage } from '@/routes/recipes/history';
import { CookIndexPage } from '@/routes/cook/cook-index';
import { CookingLogPage } from '@/routes/cook/cooking-log';
import { NotificationsPage } from '@/routes/notifications/notifications';
import { HelpPage } from '@/routes/help-page';
import { ProfilePage } from '@/routes/profile/profile';
import { SecurityPage } from '@/routes/profile/security';
import { DataPage } from '@/routes/profile/data';
import { OfflinePage } from '@/routes/offline/offline';
import { TastingVisitsPage } from '@/routes/tasting/visits-page';
import { TastingVisitDetailPage } from '@/routes/tasting/visit-detail-page';
import { TastingPlaceDetailPage, TastingPlacesPage } from '@/routes/tasting/places-page';
import { TastingSuggestionsPage } from '@/routes/tasting/suggestions-page';
import { CatalogCardsPage } from '@/routes/catalog/cards-page';
import { CatalogProductsPage, CatalogStoresPage } from '@/routes/catalog/catalog-pages';
import { CatalogIngredientsPage } from '@/routes/catalog/ingredients-page';
import { CatalogIngredientPage, CatalogProductPage } from '@/routes/catalog/detail-pages';
import { CatalogScanPage } from '@/routes/catalog/scan-page';

// Heavy screens are code-split.
const recipeEditor = lazyPage(() => import('@/routes/recipes/editor'), 'RecipeEditorPage');
const cookingMode = lazyPage(() => import('@/routes/cook/cooking-mode'), 'CookingModePage');
const tastingCapture = lazyPage(
  () => import('@/routes/tasting/capture-page'),
  'TastingCapturePage',
);

/**
 * Route map for the whole product. Screens are implemented under src/routes/<area>/.
 * Until a screen exists it renders PlaceholderPage so navigation and guards can be
 * exercised end-to-end.
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // Public
      {
        path: 'login',
        element: (
          <RedirectIfAuthenticated>
            <LoginPage />
          </RedirectIfAuthenticated>
        ),
      },
      {
        path: 'register',
        element: (
          <RedirectIfAuthenticated>
            <RegisterPage />
          </RedirectIfAuthenticated>
        ),
      },
      { path: 'auth/callback', element: <OidcCallbackPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      { path: 'verify-email', element: <VerifyEmailPage /> },
      { path: 'r/:slug', element: <PublicRecipePage /> },
      { path: 'invitations/:token', element: <InvitationPage /> },

      // Authenticated
      {
        element: <RequireAuth />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'recipes', element: <RecipeLibraryPage /> },
          { path: 'recipes/new', element: recipeEditor },
          { path: 'recipes/:id', element: <RecipeDetailPage /> },
          { path: 'recipes/:id/edit', element: recipeEditor },
          { path: 'recipes/:id/history', element: <RecipeHistoryPage /> },
          { path: 'cook/:id', element: cookingMode },
          { path: 'cook', element: <CookIndexPage /> },
          { path: 'add', element: <AddMenuPage /> },
          { path: 'add/import', element: <ImportUrlPage /> },
          { path: 'add/import/:jobId', element: <ImportJobPage /> },
          { path: 'add/text', element: <TextGenerationPage /> },
          { path: 'add/images', element: <ImagesGenerationPage /> },
          { path: 'add/video', element: <VideoGenerationPage /> },
          { path: 'add/ask', element: <AskSourcesPage /> },
          { path: 'add/ai/:generationId', element: <AiReviewPage /> },
          { path: 'assistant', element: <ConversationsPage /> },
          { path: 'assistant/:conversationId', element: <ConversationPage /> },
          { path: 'collections', element: <CollectionsPage /> },
          { path: 'collections/:id', element: <CollectionDetailPage /> },
          { path: 'shopping', element: <ShoppingListsPage /> },
          // Static segments first: `receipts` is not a list id.
          { path: 'shopping/receipts', element: <ReceiptsPage /> },
          { path: 'shopping/receipts/new', element: <ReceiptCapturePage /> },
          { path: 'shopping/receipts/:id', element: <ReceiptReviewPage /> },
          { path: 'shopping/:id', element: <ShoppingListPage /> },
          { path: 'meal-plan', element: <MealPlanPage /> },
          { path: 'pantry', element: <PantryPage /> },
          { path: 'households', element: <HouseholdsPage /> },
          { path: 'households/:id', element: <HouseholdDetailPage /> },
          { path: 'cooking-log', element: <CookingLogPage /> },
          { path: 'notifications', element: <NotificationsPage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'help', element: <HelpPage /> },
          { path: 'profile/security', element: <SecurityPage /> },
          { path: 'profile/data', element: <DataPage /> },
          { path: 'offline', element: <OfflinePage /> },
          { path: 'catalog', element: <Navigate to="/catalog/ingredients" replace /> },
          { path: 'catalog/scan', element: <CatalogScanPage /> },
          { path: 'catalog/ingredients', element: <CatalogIngredientsPage /> },
          { path: 'catalog/ingredients/:id', element: <CatalogIngredientPage /> },
          { path: 'catalog/products', element: <CatalogProductsPage /> },
          { path: 'catalog/products/:id', element: <CatalogProductPage /> },
          { path: 'catalog/stores', element: <CatalogStoresPage /> },
          { path: 'catalog/stores/:id', element: <CatalogStorePricesPage /> },
          { path: 'catalog/cards', element: <CatalogCardsPage /> },
          { path: 'tasting', element: <TastingVisitsPage /> },
          { path: 'tasting/new', element: tastingCapture },
          { path: 'tasting/visits/:visitId', element: <TastingVisitDetailPage /> },
          { path: 'tasting/places', element: <TastingPlacesPage /> },
          { path: 'tasting/places/:placeId', element: <TastingPlaceDetailPage /> },
          {
            path: 'tasting/notes/:noteId/suggestions/:generationId',
            element: <TastingSuggestionsPage />,
          },
          {
            path: 'admin/support',
            element: <RequireSupportStaff />,
            children: [
              { index: true, element: <AdminSupportPage /> },
              { path: ':id', element: <AdminSupportConversationPage /> },
            ],
          },
          {
            path: 'admin',
            element: <RequireAdmin />,
            children: [
              { index: true, element: <AdminDashboardPage /> },
              { path: 'users', element: <AdminUsersPage /> },
              { path: 'users/:id', element: <AdminUserDetailPage /> },
              { path: 'jobs', element: <AdminJobsPage /> },
              { path: 'flags', element: <AdminFlagsPage /> },
              { path: 'ai', element: <AdminAiProvidersPage /> },
              { path: 'nutrition', element: <AdminNutritionPage /> },
              { path: 'units', element: <AdminUnitsPage /> },
              { path: 'categories', element: <AdminCategoriesPage /> },
              { path: 'scraper', element: <AdminScraperPage /> },
              { path: 'reports', element: <AdminReportsPage /> },
              { path: 'audit', element: <AdminAuditPage /> },
              { path: 'mail', element: <AdminMailPage /> },
              { path: 'mail/templates', element: <AdminMailTemplatesPage /> },
              { path: 'mail/templates/:key/:locale', element: <AdminMailTemplateEditorPage /> },
              { path: 'mail/delivery', element: <AdminMailDeliveryPage /> },
              { path: 'help', element: <AdminHelpPage /> },
            ],
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
